import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  useWindowDimensions,
  Platform,
} from 'react-native';
import React, { useEffect, useState, useMemo } from 'react';
import { GTFonts, theme } from '../../../theme';
import { useAppSelector } from '../../../redux/helper';
import {
  formatMoney,
  formatNumberDecimal,
  formatWithCommas,
  fs,
  replaceCurrencyString,
  s,
  v,
  borderWidth,
  isNumericEffectivelyZero,
  roundToNumericPrecision,
} from '../../../utils/libs';
import { RootState } from '../../../redux/types';
import { isTablet } from 'react-native-device-info';
import { formatDisplayDateTime } from '../../../utils/formatDisplayDateTime';
import { getOrderLineProductDisplayName } from '../../../utils/productDisplayName';
import {
  buildReceiptTotalsSummary,
  unpaidLineAmount,
  unpaidLinesTotal,
} from '../../../services/receiptTotals';
import type { ReceiptTotalsMode } from '../../../services/receiptTotals';

// Define types for the props
interface ReceiptProps {
  screenName: string;
  order: OrderProductItem[];
  Payment_Information: Payment_Information;
  minHeightFactor: number;
  maxHeightFactor: number;
}

// Define types for the order items and total
export interface OrderProductItem {
  bp_product_information: {
    bp_product_name: string;
    bp_product_mass: string;
    bp_product_unit_of_mass: string;
    bp_product_size?: string;
    bp_product_model?: string;
    bp_product_selling_price: number;
    _id: string;
    bp_product_price_type?: 'item' | 'unit';
    bp_product_image?: string;
    bp_variant_id?: string;
    bp_product_has_variants?: boolean;
    bp_product_parent_name?: string;
  };
  bp_variant_id?: string;
  bp_order_quantity: number;
  bp_order_paid_quantity: number;
  bp_order_mass?: number;
  bp_order_paid_mass?: number;
  /** Unit rate the server will charge; set by partialPaymentsSlice. */
  bp_line_unit_price?: number;
}

export interface Payment_Information {
  bp_payment_amount: number;
  bp_payment_already_paid_amount: number;
  bp_payment_amount_due?: number;
  bp_payment_amount_refunded?: number;
  bp_current_bill_amount?: number;
  bp_payment_amount_received?: number;
  bp_amount_paid?: number | string;
  bp_payment_refunds?: Array<{
    amount?: number | string | null;
    payment_id?: string | number | null;
  }>;
  bp_payments?: Array<{
    _id?: string | number;
    amount?: number | string;
    method?: string;
  }>;
  // bp_payment_discount: number;
}

export interface DynamicTableParams {
  receipt: {
    order: OrderProductItem[];
    orderID: string;
    Payment_Information: Payment_Information;
    ReceiptID: string;
  };
  business: any;
  employeedata: any;
  currency: string;
  /** Defaults to 'print' (order ledger). PaymentModal uses 'print-tx'. */
  totalsMode?: ReceiptTotalsMode;
  /** Current location's logo; printed at the top of thermal receipts. */
  logoUrl?: string;
}

const getOrderLineDisplayName = (item: OrderProductItem): string =>
  getOrderLineProductDisplayName(item);

export const DynamicTable = ({
  receipt,
  business,
  employeedata,
  currency,
}: DynamicTableParams): string => {
  const currencyText = replaceCurrencyString('Ksh', currency);

  const date = formatDisplayDateTime(new Date());

  const calculatePartialTotals = (order: OrderProductItem[]) => {
    return order.reduce((total, Item) => {
      const { bp_product_information } = Item;
      // Handle unit pricing calculation
      if (bp_product_information.bp_product_price_type === 'unit') {
        const itemTotal =
          bp_product_information.bp_product_selling_price *
          ((Item.bp_order_mass ?? 0) - (Item.bp_order_paid_mass ?? 0));
        return total + itemTotal;
      }
      // Existing item pricing calculation
      const currentQuantity =
        Item.bp_order_quantity - (Item.bp_order_paid_quantity ?? 0);
      const itemTotal =
        bp_product_information.bp_product_selling_price * currentQuantity;
      return total + itemTotal;
    }, 0);
  };

  let receiptorderdetails = ``;
  if (receipt?.order) {
    for (const item of receipt.order) {
      if (typeof item === 'object' && item.bp_product_information) {
        const unitSuffix =
          item.bp_product_information.bp_product_price_type === 'unit'
            ? `${item.bp_product_information.bp_product_unit_of_mass}`
            : '';

        const itemDescription = getOrderLineDisplayName(item);
        const MAX_LINE_LENGTH = 13;

        // Split the description into lines if it exceeds the maximum length
        const descriptionLines =
          itemDescription.match(
            new RegExp('.{1,' + MAX_LINE_LENGTH + '}', 'g'),
          ) || [];

        const currQuantity = roundToNumericPrecision(
          item.bp_product_information.bp_product_price_type === 'unit'
            ? (item.bp_order_mass ?? 0) - (item.bp_order_paid_mass ?? 0)
            : item.bp_order_quantity - (item.bp_order_paid_quantity ?? 0),
        );
        const lineAmount = roundToNumericPrecision(
          item.bp_product_information.bp_product_selling_price * currQuantity,
        );

        // First line includes the price and amount
        const firstLine = `[L]${receipt.order.indexOf(item) + 1}.${
          descriptionLines[0]
        }[R]${formatNumberDecimal(currQuantity)}${unitSuffix}x${
          item.bp_product_information.bp_product_selling_price
        }[R]${formatWithCommas(lineAmount)}\n`;

        // Remaining lines just have the wrapped description
        const remainingLines = descriptionLines
          .slice(1)
          .map(line => line.trim())
          .join('\n');

        // Only add a newline if there are remaining lines
        receiptorderdetails +=
          firstLine + (remainingLines ? remainingLines + '\n' : '');
      } else {
        console.error('Item is not of type OrderItem', item);
      }
    }
  }

  let receipttotals = ``;
  const totalAmount = calculatePartialTotals(receipt.order);
  const summary = buildReceiptTotalsSummary(
    receipt?.Payment_Information,
    totalAmount,
  );

  for (const r of summary.rows) {
    const valueStr =
      r.emphasis === 'change'
        ? formatWithCommas(r.amount, 0, true)
        : formatWithCommas(r.amount);
    const opener = r.strong ? '<b>' : '';
    const closer = r.strong ? '</b>' : '';
    receipttotals += `[L][R]${opener}${r.label}${closer}[R]${opener}${valueStr}${closer}\n`;
  }

  receipttotals += `[L][R]<font size='tall'>Total</b>[R]${currencyText}${formatWithCommas(
    summary.grandTotal || totalAmount,
  )}</font>\n`;

  if (summary.isPartialLedger && summary.isPaidInFull) {
    receipttotals += `[C]<b>*** PAID IN FULL ***</b>\n`;
  }

  const descRaw = String(business?.description || '').trim();
  const DESC_LINE_CHARS = 32;
  const descLines =
    descRaw.length === 0
      ? []
      : descRaw.match(new RegExp('.{1,' + DESC_LINE_CHARS + '}', 'g')) || [
          descRaw,
        ];
  const descriptionCenteredBlock = descLines
    .map(line => `[C]<u><b><font>${line.trim()}</font></b></u>\n`)
    .join('');

  const recipttable =
    `[C]<u><b><font size='normal'>${business?.name || ''}</font></b></u>\n` +
    // `[C]<u><b><font size='normal'> ${
    //   (business?.Adrress?.bp_user_postal_code || "") +
    //   ", " +
    //   (business?.Adrress?.bp_user_town || "")
    // }</font></b></u>\n` +
    descriptionCenteredBlock +
    `[C]<u><b><font size='normal'>TEl ${
      employeedata?.bp_owner_phone_number || 'N/A'
    }</font></b></u>\n` +
    `[L]\n` +
    `[L]<b># Item[R]Price[R]Amount</b>\n` +
    `${receiptorderdetails}\n` +
    `${receipttotals}` +
    `[C]............................\n` +
    `[L]<font>Customer :</font>\n` +
    `${receipt.ReceiptID ? `[L]Receipt ID: ${receipt.ReceiptID}` : ''}\n` +
    `[L]Date:<u>${date}</u>\n` +
    `[L]<font>You were served by <b>${
      employeedata?.bp_employee_name || ''
    }</b></font>\n` +
    `[L]Thank you for choosing ${business?.name || ''}!\n` +
    `[C]<b>GOODS ONCE SOLD WILL NOT BE ACCEPTED</b>\n` +
    '[C]BizPlus POS - +254759266327\n';
  ("[C]<qrcode size='20'>www.Bpluspos.com/</qrcode>\n");

  return recipttable;
};

const Receipt: React.FC<ReceiptProps> = ({
  screenName,
  order,
  Payment_Information,
  minHeightFactor,
  maxHeightFactor,
}) => {
  const cart = useAppSelector(state => state.cart);
  const orderDetails = useAppSelector(state => state?.orderdetails.order);
  const cartItems = cart?.cartItems;
  const { height } = useWindowDimensions();
  const [hidePaidStatus, setHidePaidStatus] = useState(false);
  const currency = useAppSelector(
    state =>
      state.location.currentLocation?.bp_location_settings
        ?.bp_location_currency || 'KES',
  );
  const currencyText = useMemo(
    () => replaceCurrencyString('KSH', currency),
    [currency],
  );

  useEffect(() => {
    if (screenName === 'PartialOrdersCheckoutScreen') {
      setHidePaidStatus(true);
    }
  }, [screenName]);

  // const { employeedata } = useContext(AuthContext);
  // Ensure 'order' is an array of 'OrderItem' objects
  if (!Array.isArray(order)) {
    console.error('Order is not an array of OrderItem objects');
    return null;
  }

  const totalAmount = unpaidLinesTotal(order);

  // On-screen preview: never claim paid-in-full and never render Cash/Change
  // rows — those are transaction outcomes that only exist after the backend
  // responds. The `'preview'` mode enforces that guarantee inside the summary
  // builder so this component can't be tricked by half-populated state.
  const receiptSummary = useMemo(
    () => buildReceiptTotalsSummary(Payment_Information, totalAmount, 'preview'),
    [Payment_Information, totalAmount],
  );

  // Calculate the height needed for payment information sections
  const hasPaymentDetails = receiptSummary.rows.length > 0;
  const paymentSectionsHeight = hasPaymentDetails
    ? v(20 + receiptSummary.rows.length * 22)
    : v(40);
  const receiptMaxHeight = height * maxHeightFactor;
  const receiptMinHeight = height * minHeightFactor;
  const reserveTotalsInMaxHeight = screenName === 'PartialOrdersCheckoutScreen';
  const totalsSectionHeight = reserveTotalsInMaxHeight
    ? hasPaymentDetails
      ? v(20 + receiptSummary.rows.length * 22 + 42)
      : v(42)
    : 0;
  const receiptBodyMaxHeight = Math.max(
    receiptMinHeight,
    receiptMaxHeight - totalsSectionHeight,
  );

  // Calculate available height for ScrollView
  const availableHeight = Math.max(
    v(80),
    receiptBodyMaxHeight - paymentSectionsHeight - v(30),
  );

  return (
    <View style={styles.screen}>
      <View
        style={{
          minHeight: receiptMinHeight,
          maxHeight: receiptBodyMaxHeight,
          paddingVertical: s(10),
          borderRadius: s(8),
          padding: s(5),
          backgroundColor: theme.colors.surface,
        }}>
        {/* <View style={styles.top}></View> */}
        <View style={styles.receiptheader}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              flex: 2.5,
            }}>
            <Text style={[styles.headerItem, styles.itemname]}># </Text>
            <Text style={[styles.headerItem, styles.itemname]}>Item </Text>
            {order && (
              <Text style={[styles.itemname]}>{order.length} item(s)</Text>
            )}
          </View>
          <View style={{ flex: 1, alignItems: 'center' }}>
            <Text style={[styles.headerItem, styles.price]}>Qty</Text>
          </View>
          <View style={{ flex: 1 }}>
            <Text style={[styles.headerItem, styles.price]}>Price</Text>
          </View>
          <View style={{ flex: 1, marginLeft: s(-10) }}>
            <Text style={[styles.headerItem, styles.value]}>Amount</Text>
          </View>
        </View>
        <ScrollView
          style={{
            backgroundColor: 'white',
            maxHeight: availableHeight,
          }}
          showsVerticalScrollIndicator={true}
          nestedScrollEnabled={true}>
          {order &&
            order.map((item, i) => {
              // Check if 'item' is of type 'OrderItem'
              if (typeof item.bp_product_information !== 'object') {
                console.error('bp_product_information is not an object', item);
                return null;
              }

              const currentOrderQuantity =
                item.bp_product_information?.bp_product_price_type === 'unit'
                  ? (item.bp_order_mass ?? 0) - (item.bp_order_paid_mass ?? 0)
                  : item.bp_order_quantity - item.bp_order_paid_quantity;

              return (
                <View style={styles.box} key={i}>
                  <View style={{ flex: 3 }}>
                    <Text style={styles.itemname}>
                      <Text>
                        {i + 1}
                        {'. '}
                      </Text>
                      {getOrderLineDisplayName(item)}
                      {/* uses IIFE to find the item in the basket */}
                      {/* uses IIFE to find the item in the basket */}
                      {!hidePaidStatus &&
                      orderDetails?.orderType?.toLowerCase() === 'partial'
                        ? (() => {
                            const foundItem = cartItems.find(
                              cartItem =>
                                cartItem.productID ===
                                  item.bp_product_information._id &&
                                (cartItem.variantId ?? null) ===
                                  (item.bp_product_information?.bp_variant_id ??
                                    item.bp_variant_id ??
                                    null),
                            );
                            if (!foundItem) return null;
                            const priceType = foundItem.price_type;
                            const unpaidQuantity =
                              priceType === 'unit'
                                ? Number(foundItem.mass) -
                                  Number(foundItem.bp_order_paid_mass ?? 0)
                                : (foundItem.Quantity ?? 0) -
                                  (foundItem.bp_order_paid_quantity ?? 0);

                            if (
                              foundItem.bp_order_completed &&
                              isNumericEffectivelyZero(unpaidQuantity)
                            ) {
                              return <Text style={styles.paidText}>PAID</Text>;
                            } else if (unpaidQuantity > 0) {
                              return (
                                <Text style={styles.unpaidText}>
                                  {formatNumberDecimal(unpaidQuantity)}
                                  {priceType === 'unit'
                                    ? item.bp_product_information
                                        ?.bp_product_unit_of_mass
                                    : ''}
                                  {'\u00A0'}
                                  UNPAID
                                </Text>
                              );
                            }
                          })()
                        : null}
                    </Text>
                  </View>
                  <View style={{ flex: 1, alignItems: 'center' }}>
                    <Text style={styles.price}>
                      {item.bp_product_information?.bp_product_price_type ===
                      'unit' ? (
                        <>
                          {formatNumberDecimal(Number(currentOrderQuantity))}
                          <Text style={{ fontSize: fs(10) }}>
                            {'\u00A0'}
                            {
                              item.bp_product_information
                                ?.bp_product_unit_of_mass
                            }
                          </Text>
                        </>
                      ) : (
                        Number(currentOrderQuantity)
                      )}
                    </Text>
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.price}>
                      {formatMoney(
                        item.bp_product_information?.bp_product_selling_price,
                      )}
                      {item.bp_product_information?.bp_product_price_type ===
                        'unit' && (
                        <Text style={{ fontSize: fs(10) }}>
                          /
                          {item.bp_product_information?.bp_product_unit_of_mass}
                        </Text>
                      )}
                    </Text>
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.value}>
                      {formatMoney(unpaidLineAmount(item))}
                    </Text>
                  </View>
                </View>
              );
            })}
        </ScrollView>
      </View>
      <View style={{ marginTop: v(5) }}>
        <View style={styles.subtotalbox}>
          <View style={{ flex: 1 }}></View>
          <View style={{ flex: 1 }}></View>
        </View>
        {receiptSummary.rows.map((r, idx) => {
          const formatted = formatMoney(r.amount);
          return (
            <View style={styles.subtotalbox} key={`${r.label}-${idx}`}>
              <View style={{ flex: 1 }}></View>
              <View style={{ flex: 1 }}></View>
              <View style={{ flex: 2 }}>
                <Text
                  style={[
                    styles.ledgerLabel,
                    r.strong && styles.ledgerLabelStrong,
                    r.emphasis === 'balance' && styles.ledgerLabelBalance,
                  ]}>
                  {r.label}
                </Text>
              </View>
              <View style={{ flex: 1 }}>
                <Text
                  style={[
                    styles.Total,
                    r.strong && styles.ledgerLabelStrong,
                    r.emphasis === 'balance' && styles.ledgerLabelBalance,
                  ]}>
                  {formatted}
                </Text>
              </View>
            </View>
          );
        })}
        <View style={styles.totalbox}>
          <View style={{ flex: 1 }}></View>
          <View style={{ flex: 1 }}></View>
          <View style={{ flex: 2 }}>
            <Text style={styles.Total}>Sub-Total ({currencyText})</Text>
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.Total}>
              {formatMoney(receiptSummary.grandTotal || totalAmount)}
            </Text>
          </View>
        </View>
        {/*
          PAID IN FULL stamp is intentionally NOT rendered here. This component
          is used as the on-screen preview on CheckoutScreen and
          PartialOrdersCheckoutScreen. The stamp is only rendered on generated
          receipts (thermal / HTML / PDF).
        */}
        <View style={styles.totalboxdotted} />
      </View>
    </View>
  );
};

export default Receipt;

const styles = StyleSheet.create({
  screen: {
    paddingHorizontal: s(8),
  },
  Scrollview: {},
  container: {
    paddingTop: v(5),
  },
  details: {
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: v(10),
  },
  receiptheader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: v(10),
  },
  headerItem: {
    fontSize: fs(isTablet() ? 17 : 15),
    fontWeight: 'bold',
  },
  box: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: v(10),
  },
  subtotalbox: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  totalbox: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: v(10),
  },
  totalboxdotted: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: v(10),
    borderStyle: 'dotted',
    borderTopWidth: borderWidth(Platform.OS === 'web' ? 0.5 : 1),
  },
  itemname: {
    fontFamily: GTFonts.regular,
    fontSize: fs(isTablet() ? 17 : 15),
    lineHeight: fs(20),
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  paidText: {
    marginTop: v(-5),
    color: theme.colors.success,
    fontWeight: 'bold',
    fontSize: fs(10),
    marginLeft: s(-10),
  },
  unpaidText: {
    marginTop: v(-5),
    color: 'red',
    fontWeight: 'bold',
    fontSize: fs(10),
    marginLeft: s(-10),
  },
  price: {
    borderColor: 'black',
    flexDirection: 'column',
    alignItems: 'center',
    fontSize: fs(isTablet() ? 17 : 15),
    fontFamily: GTFonts.regular,
    lineHeight: fs(22),
    justifyContent: 'center',
  },
  value: {
    fontSize: fs(isTablet() ? 17 : 15),
    fontFamily: GTFonts.regular,
    lineHeight: fs(22),
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  Buttongrp: {
    justifyContent: 'center',
    alignItems: 'flex-end',
    flexDirection: 'row',
    marginBottom: v(20),
  },
  Total: {
    fontWeight: '500',
    fontSize: fs(isTablet() ? 17 : 15),
    lineHeight: fs(22),
    fontFamily: GTFonts.bold,
  },
  Ksh: {
    fontSize: fs(11),
    color: 'gray',
  },
  ledgerLabel: {
    fontSize: fs(14),
    lineHeight: fs(20),
  },
  ledgerLabelStrong: {
    fontWeight: '600',
  },
  ledgerLabelBalance: {
    color: '#b91c1c',
    fontWeight: '600',
  },
});
